/**
 * useCampaign — public/owner fetch for a single campaign by id.
 *
 * Backed by GET /api/campaigns/{id}, which:
 *   - returns 200 to anyone for status="live"
 *   - returns 200 to the owner (or admin) for any status
 *   - returns 404 to everyone else (draft/pending existence is masked)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

/** Public path for a campaign — vanity slug (myrichaunt.org/leo) when assigned, id route otherwise. */
export const campaignPath = (c) => (c?.slug ? `/${c.slug}` : `/campaign/${c?.id}`);
export const campaignUrl = (c) => `${window.location.origin}${campaignPath(c)}`;
/** Bot-friendly link (server-rendered photo + headline card); people are redirected to the vanity page. */
export const campaignShareUrl = (c) => `${process.env.REACT_APP_BACKEND_URL}/api/s/${c?.slug || c?.id}`;

/** Load by campaign id, or by vanity slug when `slug` is given. */
export const useCampaign = (id, slug = null) => {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!id && !slug) return;
    setLoading(true);
    setError("");
    try {
      const res = await client.get(slug ? `/campaigns/by-slug/${encodeURIComponent(slug)}` : `/campaigns/${id}`);
      setCampaign(res.data);
    } catch (err) {
      setError(err?.response?.status === 404 ? "not_found" : "unknown");
      setCampaign(null);
    } finally {
      setLoading(false);
    }
  }, [id, slug]);

  useEffect(() => { load(); }, [load]);

  return { campaign, loading, error, refresh: load };
};
